/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Tag, Button, Card, Row, Col, Divider, Collapse, Badge } from 'antd';
import moment from 'moment';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { DoctorScheduleColum } from '../../../utils/TableData';
// import Calender from '../../../components/Calander/Calander';
import { Breadcrumb } from '../../../components/Card/Cards';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import { CustomTable } from '../../../components/Table';
import { CommonHeadingTxt } from '../../../components/Text/Text';
// import TimelineCard from '../../../components/TimeLine';
import { StaffProfileIcon } from '../../../components/imagepath';
import { GetStaffProfileAction } from '../../../store/Actions/StaffAction';
import Loader from '../../../components/loader';
import CalanderModal from '../../../components/Modal/CalanderModal';
import { DOCS_URL } from '../../../utils/globals';

// Custom hook for staff profile data
const useStaffProfile = id => {
  const dispatch = useDispatch();
  const { StaffProfile, loader } = useSelector(state => state?.Staff);

  useEffect(() => {
    if (id) {
      dispatch(GetStaffProfileAction({ Query: id }));
    }
  }, [dispatch, id]);

  const staff = useMemo(() => StaffProfile?.[0] || null, [StaffProfile]);

  const staffType = useMemo(
    () => ({
      isTherapist: staff?.type === 'Therapist',
      isDoctor: staff?.type === 'Doctor',
    }),
    [staff?.type]
  );

  return { staff, loader, staffType, StaffProfile };
};

// Group permissions by category (e.g., user.index -> user)
const useGroupedPermissions = permissions => {
  return useMemo(() => {
    if (!permissions?.length) return {};

    const grouped = {};
    permissions.forEach(perm => {
      const [category] = perm.split('.');
      const formattedCategory = category
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (!grouped[formattedCategory]) {
        grouped[formattedCategory] = [];
      }
      grouped[formattedCategory].push(perm);
    });

    // Sort categories alphabetically
    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});
  }, [permissions]);
};

// Permission action tag colors
const getActionColor = action => {
  const colors = {
    index: 'blue',
    create: 'green',
    edit: 'orange',
    update: 'cyan',
    delete: 'red',
  };
  return colors[action] || 'default';
};

// Permissions Card Component
const PermissionsCard = ({ permissions = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const groupedPermissions = useGroupedPermissions(permissions);
  const categories = Object.keys(groupedPermissions);

  if (!permissions.length) return null;

  const collapseItems = categories.map(category => ({
    key: category,
    label: (
      <div className="d-flex align-items-center justify-content-between">
        <span style={{ fontWeight: 500 }}>{category}</span>
        <Badge
          count={groupedPermissions[category].length}
          style={{ backgroundColor: '#2E37A4' }}
        />
      </div>
    ),
    children: (
      <div className="d-flex flex-wrap gap-2">
        {groupedPermissions[category].map((perm, idx) => {
          const action = perm.split('.')[1] || perm;
          return (
            <Tag key={idx} color={getActionColor(action)} style={{ margin: 0 }}>
              {action}
            </Tag>
          );
        })}
      </div>
    ),
  }));

  return (
    <Card
      className="mb-3"
      title={
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FeatherIcon icon="shield" size={18} className="me-2" style={{ color: '#2E37A4' }} />
            <span>Permissions</span>
            <Badge
              count={permissions.length}
              style={{ backgroundColor: '#52c41a', marginLeft: 10 }}
            />
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </div>
      }
    >
      <p className="text-muted mb-3" style={{ fontSize: 13 }}>
        {categories.length} categories with {permissions.length} total permissions
      </p>
      <Collapse
        accordion={!expanded}
        activeKey={expanded ? categories : undefined}
        items={collapseItems}
        size="small"
        style={{ maxHeight: 400, overflowY: 'auto' }}
      />
    </Card>
  );
};

// Reusable detail item component
const DetailItem = ({ icon, label, value, isLink = false }) => (
  <div className="d-flex align-items-center mb-3">
    <div
      className="d-flex align-items-center justify-content-center me-3"
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: '#f0f5ff',
      }}
    >
      <FeatherIcon icon={icon} size={16} style={{ color: '#2E37A4' }} />
    </div>
    <div>
      <small className="text-muted d-block">{label}</small>
      {isLink ? (
        <Link to="" style={{ fontWeight: 500 }}>
          {value || '---'}
        </Link>
      ) : (
        <span style={{ fontWeight: 500 }}>{value || '---'}</span>
      )}
    </div>
  </div>
);

// Info Card Section component
const InfoSection = ({ title, icon, children }) => (
  <Card
    className="mb-3"
    styles={{ body: { padding: '20px' } }}
    title={
      <div className="d-flex align-items-center">
        <FeatherIcon icon={icon} size={18} className="me-2" style={{ color: '#2E37A4' }} />
        <span>{title}</span>
      </div>
    }
  >
    {children}
  </Card>
);

// Breadcrumb config
const BREADCRUMB_ITEMS = [
  { name: 'Staff', linkTo: '/staff-list?page=1' },
  { name: 'Staff List', linkTo: '/staff-list?page=1' },
  { name: 'Staff Profile' },
];

const StaffProfile = () => {
  const { id } = useParams();
  const [modal, setModal] = useState(false);
  const { staff, loader, staffType, StaffProfile: StaffProfileData } = useStaffProfile(id);

  const handleOpenModal = useCallback(() => setModal(true), []);
  const handleCloseModal = useCallback(() => setModal(false), []);

  // Profile image with fallback
  const profileImage = staff?.profileImage?.[0]
    ? DOCS_URL + staff.profileImage[0]
    : StaffProfileIcon;

  // Table columns for doctor section
  const educationColumn = [{ title: 'Degree', dataIndex: 'education' }];
  const experienceColumn = [
    {
      title: 'Experience',
      dataIndex: 'expierence',
      render: () => <span>{staff?.expierence} Years</span>,
    },
  ];
  const positionColumn = [{ title: 'Position', dataIndex: 'position' }];

  if (!staff) {
    return <Loader IsModal={true} />;
  }

  return (
    <>
      <Loader IsModal={loader} />
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb page={BREADCRUMB_ITEMS} />

          {/* Profile Header Card */}
          <Card className="mb-4">
            <Row gutter={[24, 24]} align="middle">
              {/* Profile Image */}
              <Col xs={24} md={4} className="text-center">
                <img
                  src={profileImage}
                  alt={staff?.name}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid #f0f5ff',
                  }}
                />
              </Col>

              {/* Name & Status */}
              <Col xs={24} md={12}>
                <h2 className="mb-2" style={{ fontSize: 24, fontWeight: 600 }}>
                  {staff?.name || '---'}
                </h2>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Tag color="geekblue" style={{ fontSize: 13 }}>
                    {staff?.type || '---'}
                  </Tag>
                  {staff?.role?.roleName && (
                    <Tag color="purple" style={{ fontSize: 13 }}>
                      {staff.role.roleName}
                    </Tag>
                  )}
                  <Tag color={staff?.isActive ? 'success' : 'error'} style={{ fontSize: 13 }}>
                    {staff?.isActive ? '● Active' : '○ Inactive'}
                  </Tag>
                  <Tag color={staff?.availability ? 'processing' : 'warning'} style={{ fontSize: 13 }}>
                    {staff?.availability ? '● Available' : '○ Unavailable'}
                  </Tag>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Member since {moment(staff?.createdAt).format('DD MMM YYYY')}
                </p>
              </Col>

              {/* Edit Button */}
              <Col xs={24} md={8} className="text-md-end">
                <Link
                  to={`/staff-list/staff-profile/edit-profile/${id}`}
                  className="btn btn-primary btn-rounded"
                >
                  <FeatherIcon icon="edit-2" size={14} className="me-2" />
                  Edit Profile
                </Link>
              </Col>
            </Row>
          </Card>

          {/* Details Row */}
          <Row gutter={[16, 16]}>
            {/* Contact Information */}
            <Col xs={24} md={12} lg={8}>
              <InfoSection title="Contact Information" icon="user">
                <DetailItem icon="phone" label="Phone Number" value={staff?.phoneNo} isLink />
                <DetailItem icon="mail" label="Email Address" value={staff?.email} isLink />
                <DetailItem icon="map-pin" label="Gender" value={staff?.gender} />
              </InfoSection>
            </Col>

            {/* Personal Details */}
            <Col xs={24} md={12} lg={8}>
              <InfoSection title="Personal Details" icon="info">
                <DetailItem
                  icon="calendar"
                  label="Date of Birth"
                  value={staff?.dateOfBirth ? moment(staff.dateOfBirth).format('DD MMM YYYY') : null}
                />
                <DetailItem
                  icon="gift"
                  label="Age"
                  value={
                    staff?.dateOfBirth
                      ? `${moment().diff(moment(staff.dateOfBirth), 'years')} years`
                      : null
                  }
                />
                <DetailItem icon="briefcase" label="Staff Type" value={staff?.type} />
              </InfoSection>
            </Col>

            {/* Role & Access */}
            <Col xs={24} md={12} lg={8}>
              <InfoSection title="Role & Access" icon="shield">
                <DetailItem icon="award" label="Role" value={staff?.role?.roleName} />
                <DetailItem
                  icon="key"
                  label="Total Permissions"
                  value={`${staff?.role?.permission?.length || 0} permissions`}
                />
                <DetailItem
                  icon="clock"
                  label="Last Updated"
                  value={moment(staff?.updatedAt).format('DD MMM YYYY')}
                />
              </InfoSection>
            </Col>

            {/* Expertise - if available */}
            {staff?.expertise?.length > 0 && (
              <Col xs={24} md={12} lg={8}>
                <InfoSection title="Expertise" icon="star">
                  <div className="d-flex flex-wrap gap-2">
                    {staff.expertise.map((exp, idx) => (
                      <Tag key={idx} color="cyan">
                        {exp}
                      </Tag>
                    ))}
                  </div>
                </InfoSection>
              </Col>
            )}
          </Row>

          {/* Permissions Section */}
          {staff?.role?.permission?.length > 0 && (
            <Row gutter={[16, 16]} className="mt-3">
              <Col xs={24}>
                <PermissionsCard permissions={staff.role.permission} />
              </Col>
            </Row>
          )}

          {/* Doctor Section: Education, Experience, Position */}
          {staffType.isDoctor && (
            <Row gutter={[16, 16]} className="mt-3">
              <Col xs={24}>
                <Card>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <FeatherIcon icon="book-open" size={18} className="me-2" />
                      Education & Experience
                    </h5>
                    <Link
                      to={`/staff-list/staff-profile/add-education/${id}?page=1`}
                      className="btn btn-primary btn-rounded btn-sm"
                    >
                      <i className="fa fa-plus me-1" /> Add Education
                    </Link>
                  </div>
                  <Divider className="my-3" />
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <h6 className="text-muted mb-2">Education</h6>
                      <CustomTable
                        column={educationColumn}
                        dataSource={StaffProfileData}
                        CurrentPageNum={1}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <h6 className="text-muted mb-2">Experience</h6>
                      <CustomTable
                        column={experienceColumn}
                        dataSource={StaffProfileData}
                        CurrentPageNum={1}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <h6 className="text-muted mb-2">Position</h6>
                      <CustomTable
                        column={positionColumn}
                        dataSource={StaffProfileData}
                        CurrentPageNum={1}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          {/* Schedule Section - for non-therapists */}
          {!staffType.isTherapist && (
            <Row gutter={[16, 16]} className="mt-3">
              <Col xs={24} lg={16}>
                <Card>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <FeatherIcon icon="calendar" size={18} className="me-2" />
                      Schedule
                    </h5>
                    <Button type="primary" onClick={handleOpenModal}>
                      <FeatherIcon icon="plus" size={14} className="me-1" />
                      Add Schedule
                    </Button>
                  </div>
                  <Divider className="my-3" />
                  <CustomTable
                    column={DoctorScheduleColum}
                    dataSource={staff?.schedule}
                    CurrentPageNum={1}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Calendar Modal */}
          <CalanderModal
            IsOpen={modal}
            Isclose={handleCloseModal}
            staffId={id}
            value={staff?.schedule}
          />

          {/* Commented out extra components - uncomment if needed */}
          {/* <Calender /> */}
          {/* <TimelineCard /> */}
        </div>
      </div>
    </>
  );
};

export default StaffProfile;
